import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Gift, ArrowLeft, Mail, Phone, MessageCircle, CheckCircle2, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { CONTACT, waLink } from "../data/content";
import { EXPERIENCES } from "../data/experiences";
import { ContactButton, EmailWithCopy } from "../components/site/ContactFormDialog";
import { api, formatApiError } from "../lib/api";

const TYPES = [
  { k: "proche", t: "Offrir à un proche", d: "Vous recevez le code et le bon T'POZ par e-mail, et vous les remettez à la personne de votre choix." },
  { k: "heure_suspendue", t: "Heure suspendue", d: "Vous offrez un moment à une femme que vous ne connaissez pas. Le bon est conservé par T'POZ et attribué à une femme orientée par le réseau." },
  { k: "groupe", t: "Pour un groupe de femmes", d: "Structure, entreprise ou collectif : offrez des T'POZ à plusieurs femmes." },
];

const inputCls =
  "w-full rounded-xl border border-pblv-ink/15 bg-white px-4 py-2.5 text-sm text-pblv-ink outline-none transition-colors focus:border-pblv-mauve focus:ring-2 focus:ring-pblv-mauve/20";

const labelCls = "mb-1 block text-xs font-semibold uppercase tracking-wide text-pblv-inksoft";

export default function OffrirTpoz() {
  const [params] = useSearchParams();
  const fromFiche = Boolean(params.get("offre"));
  const options = useMemo(
    () =>
      EXPERIENCES.flatMap((exp) =>
        (exp.offres || []).map((o, i) => ({
          key: `${exp.id}-${i}`,
          label: `${exp.nom} — ${o.partenaire}`,
          offre: `${exp.nom} (${o.partenaire}, ${o.lieu})`,
          partenaire: o.partenaire,
          tel: o.tel || "",
          avantage: o.avantage || exp.avantage || "",
          delai: o.delai_reservation || "",
        }))
      ),
    []
  );
  const [expKey, setExpKey] = useState("");
  const [cadeauType, setCadeauType] = useState("proche");
  const [form, setForm] = useState({
    offreur_nom: "", offreur_email: "", offreur_tel: "",
    benef_prenom: "", benef_nom: "", benef_email: "", benef_tel: "",
    commune: "", cgu: false,
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const chosen = fromFiche
    ? { offre: params.get("offre"), partenaire: params.get("partenaire") || "", tel: params.get("tel") || "", avantage: params.get("avantage") || "", delai: params.get("delai") || "" }
    : options.find((o) => o.key === expKey);

  const submit = async (e) => {
    e.preventDefault();
    if (!chosen) {
      toast.error("Choisissez l'expérience à offrir.");
      return;
    }
    setSending(true);
    try {
      const benefLabel = { heure_suspendue: ["Heure suspendue", ""], groupe: ["Groupe de femmes", ""] };
      const [p, n] = cadeauType === "proche" ? [form.benef_prenom, form.benef_nom] : benefLabel[cadeauType];
      await api.post("/code-demandes", {
        prenom: p, nom: n,
        email: form.offreur_email, tel: form.offreur_tel,
        commune: form.commune,
        offre: chosen.offre, partenaire: chosen.partenaire, partenaire_tel: chosen.tel,
        avantage: chosen.avantage, delai_reservation: chosen.delai,
        cadeau_type: cadeauType,
        offreur_nom: form.offreur_nom, offreur_email: form.offreur_email, offreur_tel: form.offreur_tel,
        benef_email: form.benef_email, benef_tel: form.benef_tel,
        attestation: true, cgu: form.cgu,
      });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Envoi impossible");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-pblv-bg text-pblv-ink">
      <header className="border-b border-pblv-ink/8 bg-white/70">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-5">
          <Link to="/" data-testid="offrir-back" className="inline-flex items-center gap-2 text-sm font-semibold text-pblv-mauve">
            <ArrowLeft className="h-4 w-4" /> Retour au site
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-pblv-mauve">
          <Gift className="h-4 w-4" /> Offrir
        </p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl">Offrir une T'POZ</h1>
        <p className="mt-4 text-base leading-relaxed text-pblv-inksoft">
          Choisissez une expérience, remplissez le formulaire : après validation par l'association, vous recevez le
          code et le bon T'POZ par e-mail et par SMS. L'achat se fait ensuite directement chez le partenaire avec ce
          code — si l'expérience réunit plusieurs partenaires, le paiement se fait via T'POZ.
        </p>

        {sent ? (
          <div className="mt-8 rounded-3xl border border-pblv-ink/8 bg-white p-8 text-center" data-testid="offrir-success">
            <CheckCircle2 className="mx-auto h-12 w-12 text-pblv-mauve" strokeWidth={1.5} />
            <h2 className="mt-4 font-serif text-2xl">Merci ! Votre demande est bien envoyée.</h2>
            <p className="mt-2 text-sm text-pblv-inksoft">
              L'association va la valider. Vous recevrez ensuite le code T'POZ par e-mail et par SMS
              {cadeauType === "heure_suspendue"
                ? ". Le bon sera conservé par T'POZ et attribué à une femme orientée par le réseau."
                : ", avec le bon T'POZ en PDF à remettre à la bénéficiaire."}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-6 rounded-3xl border border-pblv-ink/8 bg-white p-6 sm:p-8" data-testid="offrir-form">
            <div>
              <p className={labelCls}>Type de cadeau *</p>
              <div className="grid gap-2 sm:grid-cols-3">
                {TYPES.map((t) => (
                  <button
                    type="button"
                    key={t.k}
                    onClick={() => setCadeauType(t.k)}
                    data-testid={`offrir-type-${t.k}`}
                    className={`rounded-2xl border p-3.5 text-left text-sm transition-colors ${
                      cadeauType === t.k ? "border-pblv-mauve bg-pblv-mauve/10" : "border-pblv-ink/10 hover:border-pblv-mauve/40"
                    }`}
                  >
                    <span className="font-bold">{t.t}</span>
                    <span className="mt-1 block text-xs text-pblv-inksoft">{t.d}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={labelCls}>Expérience à offrir *</label>
              {fromFiche ? (
                <p data-testid="offrir-exp-fixe" className="inline-flex items-center gap-2 rounded-full bg-pblv-mauve/10 px-4 py-2 text-sm font-semibold text-pblv-mauve">
                  <Sparkles className="h-4 w-4" /> {chosen?.offre}
                </p>
              ) : (
                <select required value={expKey} onChange={(e) => setExpKey(e.target.value)} data-testid="offrir-exp-select" className={inputCls}>
                  <option value="">— Choisir une expérience —</option>
                  {options.map((o) => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
              )}
            </div>

            <div className="grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-3"><p className="text-sm font-bold">Vos coordonnées</p></div>
              <div><label className={labelCls}>Nom *</label><input data-testid="offreur-nom" required value={form.offreur_nom} onChange={set("offreur_nom")} className={inputCls} /></div>
              <div><label className={labelCls}>E-mail *</label><input data-testid="offreur-email" required type="email" value={form.offreur_email} onChange={set("offreur_email")} className={inputCls} /></div>
              <div><label className={labelCls}>Téléphone *</label><input data-testid="offreur-tel" required type="tel" value={form.offreur_tel} onChange={set("offreur_tel")} className={inputCls} /></div>
            </div>

            {cadeauType === "proche" && (
              <div className="grid gap-3 sm:grid-cols-2" data-testid="offrir-benef-bloc">
                <div className="sm:col-span-2"><p className="text-sm font-bold">La bénéficiaire</p></div>
                <div><label className={labelCls}>Prénom *</label><input data-testid="benef-prenom" required value={form.benef_prenom} onChange={set("benef_prenom")} className={inputCls} /></div>
                <div><label className={labelCls}>Nom *</label><input data-testid="benef-nom" required value={form.benef_nom} onChange={set("benef_nom")} className={inputCls} /></div>
                <div><label className={labelCls}>E-mail (facultatif)</label><input data-testid="benef-email" type="email" value={form.benef_email} onChange={set("benef_email")} className={inputCls} /></div>
                <div><label className={labelCls}>Téléphone (facultatif)</label><input data-testid="benef-tel" type="tel" value={form.benef_tel} onChange={set("benef_tel")} className={inputCls} /></div>
              </div>
            )}

            <label className="flex items-start gap-2 text-sm text-pblv-inksoft">
              <input type="checkbox" required checked={form.cgu} onChange={set("cgu")} data-testid="offrir-cgu" className="mt-1" />
              <span>J'accepte les <Link to="/conditions" className="text-pblv-mauve underline underline-offset-2">conditions d'utilisation</Link> de T'POZ. *</span>
            </label>

            <button
              type="submit"
              disabled={sending}
              data-testid="offrir-submit"
              className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-pblv-mauve px-7 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-pblv-mauvedark disabled:opacity-60"
            >
              {sending && <Loader2 className="h-4 w-4 animate-spin" />} Envoyer ma demande d'offre
            </button>
            <p className="text-xs text-pblv-inksoft">
              Le règlement se fait directement chez le partenaire avec le code. Si l'expérience réunit plusieurs
              partenaires, le paiement se fait via T'POZ.
            </p>
          </form>
        )}

        <div className="mt-8 rounded-3xl border border-pblv-ink/8 bg-white p-6">
          <p className="text-sm font-bold">Une question ? Contactez l'association</p>
          <div className="mt-3 flex flex-col gap-2 text-sm">
            <ContactButton
              subject="Offrir des T'POZ"
              testId="offrir-form-btn"
              className="inline-flex items-center gap-3 rounded-xl bg-pblv-mauve/10 px-4 py-3 text-sm font-semibold text-pblv-ink transition-colors hover:bg-pblv-mauve/20"
            >
              <Mail className="h-4 w-4 text-pblv-mauve" /> Écrire à l'association
            </ContactButton>
            <p className="px-4 text-sm font-semibold text-pblv-ink">
              <EmailWithCopy testId="offrir-email" className="text-pblv-mauve" />
            </p>
            <a href={CONTACT.phoneHref} data-testid="offrir-tel" className="inline-flex items-center gap-3 rounded-xl px-4 py-2 font-semibold hover:bg-pblv-bg">
              <Phone className="h-4 w-4 text-pblv-mauve" /> {CONTACT.phone}
            </a>
            <a href={waLink("Bonjour, je souhaite offrir une T'POZ.")} target="_blank" rel="noreferrer" data-testid="offrir-wa" className="inline-flex items-center gap-3 rounded-xl px-4 py-2 font-semibold hover:bg-pblv-bg">
              <MessageCircle className="h-4 w-4 text-pblv-mauve" /> WhatsApp
            </a>
          </div>
        </div>
      </main>
    </div>
  );
}
